import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  BriefcaseIcon,
  StarIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { API_BASE } from '../lib/config';

const ProfilePage = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    location: user?.location || '',
    bio: user?.bio || '',
    self_introduction: user?.self_introduction || '',
    skills: user?.skills || [],
    gender: user?.gender || 'not_applicable'
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSkillsChange = (e) => {
    const skills = e.target.value.split(',').map(skill => skill.trim()).filter(skill => skill);
    setFormData(prev => ({
      ...prev,
      skills
    }));
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('画像サイズは5MB以下にしてください');
      return;
    }

    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64String = reader.result;

        // Upload
        const response = await fetch(`${API_BASE}/users/profile-image`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ image: base64String })
        });

        if (response.ok) {
          toast.success('プロフィール画像を更新しました');
          // Update context
          updateUser({ ...user, profile_image: base64String, icon_url: base64String });
        } else {
          toast.error('画像のアップロードに失敗しました');
        }
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error(err);
      toast.error('エラーが発生しました');
    }
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');

      // Use dedicated /mypage/profile endpoint
      const response = await fetch(`${API_BASE}/mypage/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          location: formData.location,
          bio: formData.bio,
          self_introduction: formData.self_introduction,
          skills: formData.skills,
          gender: formData.gender
        })
      });

      if (response.ok) {
        const data = await response.json();

        // ローカルストレージとコンテキストのユーザー情報を更新
        const updatedUser = data.user || {
          ...user,
          name: formData.name,
          phone: formData.phone,
          location: formData.location,
          bio: formData.bio,
          self_introduction: formData.self_introduction,
          skills: formData.skills,
          gender: formData.gender
        };

        // AuthContextのupdateUser関数を使用してユーザー情報を更新
        updateUser(updatedUser);

        toast.success('プロフィールを更新しました');
        setEditing(false);
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'プロフィールの更新に失敗しました');
      }
    } catch (error) {
      console.error('Profile update error:', error);
      toast.error('プロフィールの更新に失敗しました');
    }
  };

  const handleCancel = () => {
    setFormData({
      name: user?.name || '',
      phone: user?.phone || '',
      location: user?.location || '',
      bio: user?.bio || '',
      self_introduction: user?.self_introduction || '',
      skills: user?.skills || []
    });
    setEditing(false);
  };

  const renderStars = (rating) => {
    const safeRating = Math.max(0, Math.min(5, Number.isFinite(Number(rating)) ? Number(rating) : 0));
    const stars = [];
    const fullStars = Math.floor(safeRating);
    const hasHalfStar = safeRating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<StarIcon key={i} className="w-5 h-5 text-yellow-400 fill-current" />);
    }

    if (hasHalfStar) {
      stars.push(<StarIcon key="half" className="w-5 h-5 text-yellow-400 fill-current opacity-50" />);
    }

    const emptyStars = 5 - Math.ceil(safeRating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<StarIcon key={`empty-${i}`} className="w-5 h-5 text-gray-300" />);
    }

    return stars;
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            プロフィールを表示するにはログインしてください
          </h1>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back to MyPage */}
        <div className="mb-4">
          <button
            onClick={() => navigate('/mypage')}
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
            aria-label="マイページに戻る"
          >
            {/* Simple left arrow */}
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            マイページに戻る
          </button>
        </div>
        {/* Header */}

        {/* Profile Completion Bar (Job Seeker Only) */}
        {user.user_type === 'job_seeker' && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            {(() => {
              const calculateCompleteness = (u) => {
                let score = 0;
                let missing = [];
                if (u.profile_image || u.icon_url) score += 20; else missing.push('プロフィール写真');
                if (u.self_introduction && u.self_introduction.length >= 20) score += 30; else missing.push('自己紹介文');
                if (u.skills && u.skills.length > 0) score += 20; else missing.push('スキル設定');
                if (u.phone) score += 10; else missing.push('電話番号');
                if (u.is_identity_verified) score += 20; else missing.push('本人確認');
                return { score, missing };
              };
              const { score, missing } = calculateCompleteness(user);

              return (
                <div>
                  <div className="flex justify-between items-end mb-2">
                    <div>
                      <h3 className="text-lg font-bold text-gray-800">プロフィール充実度</h3>
                      <p className="text-sm text-gray-500">充実度が高いほど、採用率がアップします！</p>
                    </div>
                    <span className="text-2xl font-bold text-blue-600">{score}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
                    <div className="bg-blue-600 h-2.5 rounded-full transition-all duration-500" style={{ width: `${score}%` }}></div>
                  </div>
                  {score < 100 && (
                    <div className="text-sm bg-blue-50 text-blue-700 p-3 rounded-md flex items-start">
                      <span className="mr-2">💡</span>
                      <span>
                        <strong>次のステップ:</strong> {missing[0]}を設定して、採用率をアップさせましょう！
                      </span>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm p-8 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center overflow-hidden">
                  {user.profile_image || user.icon_url ? (
                    <img src={user.profile_image || user.icon_url} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <UserIcon className="w-10 h-10 text-blue-600" />
                  )}
                </div>
                {editing && (
                  <label className="absolute bottom-0 right-0 bg-white rounded-full p-1 shadow cursor-pointer hover:bg-gray-100">
                    <input type="file" className="hidden" accept="image/*" onChange={handleImageChange} />
                    <PencilIcon className="w-4 h-4 text-gray-600" />
                  </label>
                )}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{user.name}</h1>
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${user.user_type === 'employer'
                    ? 'bg-purple-100 text-purple-800'
                    : 'bg-green-100 text-green-800'
                    }`}>
                    {user.user_type === 'employer' ? '企業・店舗' : '求職者'}
                  </span>
                  <span></span>
                  <span>
                    登録日: {user?.created_at ? new Date(user.created_at).toLocaleDateString('ja-JP') : '未設定'}
                  </span>
                </div>
              </div>
            </div>

            <button
              onClick={() => setEditing(!editing)}
              className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <PencilIcon className="w-4 h-4" />
              <span>編集</span>
            </button>
          </div>

          {/* Stats for Job Seekers */}
          {user.user_type === 'job_seeker' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">{Number(user?.completed_jobs) || 0}</div>
                <div className="text-sm text-gray-600">完了した仕事</div>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center space-x-1 mb-1">
                  {renderStars(user?.rating)}
                </div>
                <div className="text-sm text-gray-600">
                  評価 {(Number.isFinite(Number(user?.rating)) ? Number(user.rating).toFixed(1) : '0.0')} / 5.0
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">
                  {user.skills?.length || 0}
                </div>
                <div className="text-sm text-gray-600">スキル</div>
              </div>
            </div>
          )}
        </div>

        {/* Profile Information */}
        <div className="bg-white rounded-lg shadow-sm p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">プロフィール情報</h2>
            {editing && (
              <div className="flex space-x-2">
                <button
                  onClick={handleSave}
                  className="flex items-center space-x-1 bg-green-600 text-white px-3 py-1 rounded-lg hover:bg-green-700 transition-colors text-sm"
                >
                  <CheckIcon className="w-4 h-4" />
                  <span>保存</span>
                </button>
                <button
                  onClick={handleCancel}
                  className="flex items-center space-x-1 bg-gray-600 text-white px-3 py-1 rounded-lg hover:bg-gray-700 transition-colors text-sm"
                >
                  <XMarkIcon className="w-4 h-4" />
                  <span>キャンセル</span>
                </button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <UserIcon className="w-4 h-4 inline mr-1" />
                {user.user_type === 'employer' ? '会社名・店舗名' : '氏名'}
              </label>
              {editing ? (
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="form-input"
                />
              ) : (
                <div className="text-gray-900 py-2">{user.name}</div>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <EnvelopeIcon className="w-4 h-4 inline mr-1" />
                メールアドレス
              </label>
              <div className="text-gray-900 py-2">{user.email}</div>
              <div className="text-xs text-gray-500">メールアドレスは変更できません</div>
            </div>

            {/* Gender */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                性別
              </label>
              {editing ? (
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  className="form-input"
                >
                  <option value="not_applicable">回答しない/その他</option>
                  <option value="male">男性</option>
                  <option value="female">女性</option>
                </select>
              ) : (
                <div className="text-gray-900 py-2">
                  {user.gender === 'male' ? '男性' : user.gender === 'female' ? '女性' : '回答しない/その他'}
                </div>
              )}
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <PhoneIcon className="w-4 h-4 inline mr-1" />
                電話番号
              </label>
              {editing ? (
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="090-1234-5678"
                />
              ) : (
                <div className="text-gray-900 py-2">{user.phone || '未設定'}</div>
              )}
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <MapPinIcon className="w-4 h-4 inline mr-1" />
                {user.user_type === 'employer' ? '所在地' : '希望勤務地'}
              </label>
              {editing ? (
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="東京都渋谷区"
                />
              ) : (
                <div className="text-gray-900 py-2">{user.location || '未設定'}</div>
              )}
            </div>
          </div>

          {/* Bio */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <BriefcaseIcon className="w-4 h-4 inline mr-1" />
              {user.user_type === 'employer' ? '会社・店舗の紹介' : '自己紹介'}
            </label>
            {editing ? (
              <textarea
                name="bio"
                value={formData.bio}
                onChange={handleChange}
                rows="4"
                className="form-input"
                placeholder={
                  user.user_type === 'employer'
                    ? '会社や店舗について詳しく教えてください'
                    : 'スキルや経験、働く上で大切にしていることを教えてください'
                }
              />
            ) : (
              <div className="text-gray-900 py-2 whitespace-pre-wrap">
                {user.bio || '未設定'}
              </div>
            )}
          </div>

          {/* Skills (for job seekers) */}
          {user.user_type === 'job_seeker' && (
            <>
              {/* Self Introduction for job seekers */}
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <UserIcon className="w-4 h-4 inline mr-1" />
                  自己紹介文
                  <span className="text-red-500 ml-1">*</span>
                </label>
                {editing ? (
                  <>
                    <div className="flex justify-end mb-2">
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, self_introduction: "【経歴】\n居酒屋ホール：2年\nコンビニ：1年\n\n【強み】\n明るい笑顔での接客が得意です！\n体力にも自信があります。\n\n【ひとこと】\n急なシフトにも極力対応したいと考えています。よろしくお願いします！" }))}
                        className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded hover:bg-blue-100 transition"
                      >
                        ✨ テンプレートを入力 (例)
                      </button>
                    </div>
                    <textarea
                      name="self_introduction"
                      value={formData.self_introduction}
                      onChange={handleChange}
                      rows="6"
                      className="form-input"
                      placeholder="あなたの経験、スキル、働く上で大切にしていることなど、事業者にアピールしたい内容を20文字以上で記入してください"
                      minLength="20"
                      required
                    />
                  </>
                ) : (
                  <div className="text-gray-900 py-2 whitespace-pre-wrap min-h-[60px] bg-gray-50 p-3 rounded border">
                    {user.self_introduction || (
                      <span className="text-red-500 italic">未設定（応募には自己紹介文の入力が必要です）</span>
                    )}
                  </div>
                )}
                {editing && (
                  <div className="text-xs text-gray-500 mt-1">
                    {formData.self_introduction ? formData.self_introduction.length : 0}文字 (最低20文字必要)
                  </div>
                )}
              </div>

              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  スキル・特技
                </label>
                {editing ? (
                  <div>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {['接客', 'レジ打ち', '調理補助', '清掃', '品出し', '引越し', 'イベント', 'PC入力'].map(skill => (
                        <button
                          key={skill}
                          type="button"
                          onClick={() => {
                            if (!formData.skills.includes(skill)) {
                              setFormData(prev => ({ ...prev, skills: [...prev.skills, skill] }));
                            }
                          }}
                          className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full hover:bg-gray-200"
                        >
                          + {skill}
                        </button>
                      ))}
                    </div>
                    <input
                      type="text"
                      value={formData.skills.join(', ')}
                      onChange={handleSkillsChange}
                      className="form-input"
                      placeholder="例: 接客, 英語, Excel, 運転免許 (カンマ区切り)"
                    />
                  </div>
                ) : (
                  <div className="py-2">
                    {user.skills && user.skills.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {user.skills.map((skill, index) => (
                          <span key={index} className="tag primary">
                            {skill}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div className="text-gray-500">未設定</div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Account Settings */}
        <div className="bg-white rounded-lg shadow-sm p-8 mt-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">アカウント設定</h2>

          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-gray-200">
              <div>
                <div className="font-medium text-gray-900">パスワード変更</div>
                <div className="text-sm text-gray-600">アカウントのセキュリティを保護するため、定期的にパスワードを変更してください</div>
              </div>
              <button className="text-blue-600 hover:text-blue-700 font-medium">
                変更
              </button>
            </div>

            <div className="flex items-center justify-between py-3 border-b border-gray-200">
              <div>
                <div className="font-medium text-gray-900">通知設定</div>
                <div className="text-sm text-gray-600">メール通知の設定を管理します</div>
              </div>
              <button className="text-blue-600 hover:text-blue-700 font-medium">
                設定
              </button>
            </div>

            <div className="flex items-center justify-between py-3">
              <div>
                <div className="font-medium text-red-600">アカウント削除</div>
                <div className="text-sm text-gray-600">アカウントを完全に削除します（復元不可）</div>
              </div>
              <button className="text-red-600 hover:text-red-700 font-medium">
                削除
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;